import { API } from "@swivl-mono/api-helper";
import { CUSTOM_INSTRUCTIONS_MAX_CHARS, sanitizeCustomInstructions } from "@swivl-mono/helpers";
import { CortexSetting, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, TextArea } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { Member } from "../../../../../Models/Member/Member.Current";
import { CortexSettingRow } from "../../Components/CortexSettingRow";
import { TopicCard } from "../../Components/TopicCard";
import { useTopicContext, useTopicHandlers, useTopicRow } from "../../Hooks/useTopicContext";

export const TOPIC_VARS: LocationVarKey[] = [LocationVarKey.CortexCustomInstructions];

/**
 * ENG-3357 — Swivl-staff custom instructions: the bounded free-text escape hatch
 * that becomes its own system-prompt section after the platform rules. Lives
 * at the bottom of the Policies list, staff-only (Chuck, 2026-09-18).
 *
 * The Policy Assistant is hidden behind one button. Clicking it takes over the
 * card: describe the change → preview the proposed text → Save (persists
 * immediately and the card shows the saved text), Rewrite, or Cancel
 * (Chuck, 2026-09-21).
 */
const CUSTOM_INSTRUCTIONS_SETTING: CortexSetting = {
  kind: "textarea",
  key: "customInstructions",
  varKey: LocationVarKey.CortexCustomInstructions,
  label: "Custom instructions",
  description: `Free-form instructions for the bot at this scope — conduct only, for one-off needs no structured setting covers. Facts (hours, fees, amenities) belong on their own pages; escalation and ticket rules live on Escalation & Ticketing. Platform rules on truthfulness and tool outcomes always win. Max ${CUSTOM_INSTRUCTIONS_MAX_CHARS} characters.`,
  placeholder: "e.g. When a customer mentions they are a current tenant of our sister brand Sunny Storage, welcome them as an existing customer.",
  rows: 8,
  default: ""
};

export function CustomInstructionsTopic(): React.ReactElement {
  const [assistantOpen, setAssistantOpen] = useState<boolean>(false);
  const ctx = useTopicContext();

  return (
    <TopicCard
      title="Custom instructions"
      description="Swivl staff only. One-off instructions for this business that become part of the bot's system prompt."
    >
      {assistantOpen ? (
        <PolicyAssistantView onClose={() => setAssistantOpen(false)} />
      ) : (
        <>
          <CortexSettingRow setting={CUSTOM_INSTRUCTIONS_SETTING} />
          <CharacterBudget />
          {Member.isSwivlStaff && (
            <div className="flex items-center gap-3 border-t border-grey-200 px-4 py-3">
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={() => setAssistantOpen(true)}>
                Draft with Policy Assistant
              </Button>
              <span className="body-sm text-grey-500">
                Describe the change in plain words; the assistant writes the full text for you to review and save.
              </span>
            </div>
          )}
        </>
      )}
    </TopicCard>
  );
}

/** Live character count against the cap, read from the topic draft. */
function CharacterBudget(): React.ReactElement | null {
  const { merged, draft } = useTopicRow(LocationVarKey.CortexCustomInstructions);
  const value = draft?.value ?? merged.value ?? "";
  const length = sanitizeCustomInstructions(value).length;
  if (!value) return null;
  const over = value.trim().length > CUSTOM_INSTRUCTIONS_MAX_CHARS;
  return (
    <p className={over ? "body-sm px-4 pb-3 text-red-700" : "body-sm px-4 pb-3 text-grey-500"}>
      {over
        ? `${value.trim().length} characters — over the ${CUSTOM_INSTRUCTIONS_MAX_CHARS} limit; the bot receives the first ${length} on a word boundary. Trim before saving.`
        : `${length} / ${CUSTOM_INSTRUCTIONS_MAX_CHARS} characters`}
    </p>
  );
}

interface PolicyAssistantViewProps {
  onClose: () => void;
}

type AssistantStep = "request" | "preview";

/**
 * The card's takeover view. Step 1: what should change. Step 2: the proposed
 * full text and the assistant's notes, with Save (persist now), Rewrite (back
 * to the request, text kept) and Cancel. Nothing is written until Save.
 */
function PolicyAssistantView({ onClose }: PolicyAssistantViewProps): React.ReactElement {
  const ctx = useTopicContext();
  const { merged, draft } = useTopicRow(LocationVarKey.CortexCustomInstructions);
  const handlers = useTopicHandlers(LocationVarKey.CortexCustomInstructions);
  const [step, setStep] = useState<AssistantStep>("request");
  const [request, setRequest] = useState<string>("");
  const [proposal, setProposal] = useState<{ draft: string; notes: string[] } | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const current = draft?.value ?? merged.value ?? "";

  const handleDraft = async (): Promise<void> => {
    if (!Member.orgId || !request.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await API.PolicyAssistant.draftCustomInstructions({ orgId: Member.orgId, current, request: request.trim() });
      setProposal(result);
      setStep("preview");
    } catch (err) {
      setError(err instanceof Error ? err.message : "The assistant could not be reached.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Stage the proposed text as this scope's value, then persist it. The topic's
   * save reads the staged draft from React state, so the save itself runs in the
   * effect below, once the draft actually holds the proposal.
   */
  const [pendingSave, setPendingSave] = useState<boolean>(false);
  const handleSave = (): void => {
    if (!proposal) return;
    setBusy(true);
    setError(undefined);
    if (merged.inheritance !== "none" && draft === undefined) handlers.onCustomize(current, false);
    handlers.onSettingValue(proposal.draft);
    if (ctx.explicitEditing && !ctx.isEditing) ctx.setEditing(true);
    setPendingSave(true);
  };

  useEffect(() => {
    if (!pendingSave || !proposal || draft?.value !== proposal.draft) return;
    let cancelled = false;
    void (async () => {
      try {
        await ctx.saveTopic();
        if (!cancelled) onClose();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "The instructions could not be saved.");
      } finally {
        if (!cancelled) {
          setPendingSave(false);
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSave, draft?.value]);

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="heading-base-alt text-grey-900">Policy Assistant</p>
          <p className="body-sm mt-0.5 text-grey-600">
            {step === "request"
              ? "Say what should change or be added, in plain words. The assistant writes the full instructions for you to review."
              : "Review the proposed instructions. Save writes them for this scope; Rewrite lets you change the request."}
          </p>
        </div>
        <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>

      {current && step === "request" && (
        <div className="mt-3 rounded-lg border border-grey-200 bg-grey-50 p-3">
          <p className="body-sm font-semibold text-grey-900">Current instructions</p>
          <pre className="body-sm mt-1 font-sans whitespace-pre-wrap text-grey-700">{current}</pre>
        </div>
      )}

      {step === "request" && (
        <div className="mt-3 flex flex-col gap-2">
          <TextArea
            rows={4}
            value={request}
            placeholder="e.g. Always mention that the elevator is out until October and offer ground-floor units instead."
            onChange={(e) => setRequest(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={busy || !request.trim()} onClickAsync={handleDraft}>
              {busy ? "Drafting…" : "Draft"}
            </Button>
          </div>
        </div>
      )}

      {step === "preview" && proposal && (
        <div className="mt-3 rounded-lg border border-grey-200 bg-grey-50 p-3">
          <p className="body-sm font-semibold text-grey-900">Proposed instructions</p>
          <pre className="body-sm mt-1 font-sans whitespace-pre-wrap text-grey-800">
            {proposal.draft || "(empty — the request removes everything)"}
          </pre>
          <p className="body-sm mt-1 text-grey-500">
            {sanitizeCustomInstructions(proposal.draft).length} / {CUSTOM_INSTRUCTIONS_MAX_CHARS} characters
          </p>
          {proposal.notes.length > 0 && (
            <>
              <p className="body-sm mt-3 font-semibold text-grey-900">Left out or moved</p>
              <ul className="body-sm mt-1 list-disc pl-5 text-grey-700">
                {proposal.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={busy} onClick={handleSave}>
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={busy} onClick={() => setStep("request")}>
              Rewrite
            </Button>
          </div>
        </div>
      )}

      {error && <p className="body-sm mt-2 text-red-700">{error}</p>}
    </div>
  );
}
